import React from 'react';
import { DesktopNavigation } from '@core/ui/navigation';
import { useSavedSearches } from '../../../../hooks/use-saved-searches';
import { useUserContext, useThemeContext, useUser } from '../../../../contexts';
import { transformSavedSearchesToNavItems } from './savedSearchesNavHelpers';
import { translate } from '../../../../utils/translations';
import { ThemeNames } from '../../../../types/themes';
import { headerIds } from '../../../../constants/test-constants';
import type { NavigationButton } from '@core/ui/navigation';
import type { SavedSearchesNavConfig } from './savedSearchesNavHelpers';

/**
 * SavedSearchesNav Component
 * Renders a navigation menu for saved searches with theme-aware labeling
 * Provides access to saved searches and properties in the user's account
 */
const SavedSearchesNav = () => {
  // Hooks for data and state management
  const { savedSearches, lastSearch, isLoading, prefetchSavedSearches } = useSavedSearches();
  const { isCrawler } = useUserContext();
  const { themeName } = useThemeContext();
  const { siteLocation } = useUser();
  
  // Prefetch saved searches when component mounts
  React.useEffect(() => {
    prefetchSavedSearches();
  }, [prefetchSavedSearches]);
  
  // Configure navigation based on theme and site location
  const navConfig = React.useMemo((): SavedSearchesNavConfig => ({
    myAccountLabel: themeName === ThemeNames.ZOOCASA 
      ? 'My Zoocasa' 
      : `My ${translate('Favourites', siteLocation)}`,
    viewAllSearchesPath: '/account/saved/searches',
    myAccountPath: '/account/saved/properties',
  }), [themeName, siteLocation]);
  
  // Loading state placeholder items
  const loadingItems = React.useMemo(() => [
    { id: 'loading', type: 'link' as const, label: 'Loading...', href: '#' },
  ], []);
  
  // Transform saved searches into navigation items
  const navItems = React.useMemo(() => {
    if (isLoading) return loadingItems;
    return transformSavedSearchesToNavItems(savedSearches, lastSearch, navConfig);
  }, [savedSearches, lastSearch, isLoading, loadingItems, navConfig]);

  // Configure the main navigation structure
  const navigationItems = React.useMemo((): NavigationButton[] => [
    {
      id: 'saved-searches',
      buttonVariant: 'dropdown',
      label: 'My Searches',
      testId: headerIds.mySearchesHeader,
      items: navItems,
    },
  ], [navItems]);

  // Render the desktop navigation menu
  return (
    <DesktopNavigation
      items={navigationItems}
      isCrawler={isCrawler}
      behavior={{
        dropdownTrigger: 'hover',
        closeOnOutsideClick: true,
        enableSubmenus: true,
        mobileExpandInline: false,
      }}
    />
  );
};

export default SavedSearchesNav; 